import { DocumentTypeCode, Extension } from '@quromedical/fhir-common';
import { z } from 'zod';
import { DisplayReferenceSchema, UUIDSchema } from '../domain/models';
import { PaginationSchema } from '../domain/pagination';
import { PaginatedBundleSchema } from '../domain/schema';
import { MediaDataSchema } from '../media';

export const documentStatus = ['current', 'entered-in-error'] as const;

export const documentExtensions = [
  Extension.csv,
  Extension.dcm,
  Extension.doc,
  Extension.docx,
  Extension.json,
  Extension.odt,
  Extension.pdf,
  Extension.xls,
  Extension.xlsx,
  Extension.xml,
] as const;

export const DocumentTypeSchema = z.enum([
  DocumentTypeCode.clinicalReferral,
  DocumentTypeCode.homeBasedCareReferral,
  DocumentTypeCode.motivationMedicalDevice,
  DocumentTypeCode.createScript,
]);

export const FileExtensionDocumentSchema = z.enum(documentExtensions);

export const DocumentSchema = z.object({
  id: UUIDSchema,
  patient: DisplayReferenceSchema,
  author: DisplayReferenceSchema,
  created: z.string(),
  status: z.enum(documentStatus),
  type: DocumentTypeSchema,
  document: MediaDataSchema,
});

export const DocumentMotivationMedicalDeviceSchema = z.object({
  deviceName: z.string(),
});

export const MedicationSchema = z.object({
  name: z.string(),
  nappi: z.string(),
  qty: z.string(),
});

export const DocumentCreateMedicationUsageSchema = z.object({
  medications: z.array(MedicationSchema).or(MedicationSchema),
  notes: z.string(),
});
export const DocumentCreateMedicationScriptSchema = z.object({
  medications: z.object({
    display: z.string(),
    value: z.string(),
  }),
  quantity: z.string(),
});

export const ReferralSchema = z.object({
  reasonForReferral: z.string(),
  medicalHistory: z.string(),
  currentTreatment: z.string(),
  recentInvestigations: z.string().optional(),
  externalPractitionerDiscipline: z.string(),
  externalPractitionerName: z.string(),
  externalPractitionerEmail: z.string().email().optional(),
  externalPractitionerPhoneNumber: z.string().optional(),
});

export const DocumentClinicalReferralSchema = ReferralSchema;

export const DocumentHomeBasedCareReferralSchema = ReferralSchema;

export const DocumentCreateSchema = z
  .object({
    patient: UUIDSchema,
    type: DocumentTypeSchema,
    data: z.unknown(), // Use `unknown` initially, refine later based on `type`
  })
  .superRefine((document, ctx) => {
    // Validate `data` based on `type`
    switch (document.type) {
      case DocumentTypeCode.motivationMedicalDevice: {
        const motivationMedicalDeviceResult =
          DocumentMotivationMedicalDeviceSchema.safeParse(document.data);
        if (!motivationMedicalDeviceResult.success) {
          motivationMedicalDeviceResult.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: ['data', ...issue.path],
            });
          });
        }
        break;
      }

      case DocumentTypeCode.homeBasedCareReferral: {
        const homeBasedCareReferralResult =
          DocumentHomeBasedCareReferralSchema.safeParse(document.data);
        if (!homeBasedCareReferralResult.success) {
          homeBasedCareReferralResult.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: ['data', ...issue.path],
            });
          });
        }
        break;
      }

      case DocumentTypeCode.clinicalReferral: {
        const clinicalReferralResult = DocumentClinicalReferralSchema.safeParse(
          document.data,
        );
        if (!clinicalReferralResult.success) {
          clinicalReferralResult.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: ['data', ...issue.path],
            });
          });
        }
        break;
      }

      case DocumentTypeCode.createScript: {
        const clinicalReferralResult =
          DocumentCreateMedicationUsageSchema.safeParse(document.data);
        if (!clinicalReferralResult.success) {
          clinicalReferralResult.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: ['data', ...issue.path],
            });
          });
        }
        break;
      }

      default:
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid document type',
          path: ['type'],
        });
    }
  });

export const DocumentSearchSchema = PaginationSchema.extend({
  patient: UUIDSchema.optional(),
});

export const DocumentGetAllResponseSchema = PaginatedBundleSchema.extend({
  documents: DocumentSchema.array(),
});

export type Document = z.infer<typeof DocumentSchema>;
export type DocumentCreate = z.infer<typeof DocumentCreateSchema>;
export type DocumentSearch = z.infer<typeof DocumentSearchSchema>;
export type DocumentGetAllResponse = z.infer<
  typeof DocumentGetAllResponseSchema
>;
export type DocumentMotivationMedicalDevice = z.infer<
  typeof DocumentMotivationMedicalDeviceSchema
>;
export type DocumentHomeBasedCareReferral = z.infer<
  typeof DocumentHomeBasedCareReferralSchema
>;
export type DocumentClinicalReferral = z.infer<
  typeof DocumentClinicalReferralSchema
>;
